<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { type UploadCustomRequestOptions, type UploadFileInfo } from 'naive-ui';
import { TemplateQuestionsService } from '@/services/contractGeneration/templateQuestionsService';
import { TemplateVariantsService } from '@/services/contractGeneration/templateVariantsService';
import type {
  ContractGenerationTemplateQuestionInDb,
  ContractGenerationTemplateVariantInDb,
} from '@/api';
import {
  answerDraftError,
  blankAnswerDraft,
  emptyAnswerDraft,
  toVariantAnswerPayload,
  type VariantAnswerDraftMap,
} from '@/utils/contractGeneration/questions';
import { extractErrorDetail } from '@/utils/errorHandler';
import GaiaButton from '@/components/design/base/GaiaButton.vue';
import GaiaFileUpload from '@/components/design/base/GaiaFileUpload.vue';
import GaiaFormField from '@/components/design/base/GaiaFormField.vue';
import GaiaDeleteButton from '@/components/design/base/GaiaDeleteButton.vue';
import GaiaModal from '@/components/design/base/GaiaModal.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';
import GaiaBanner from '@/components/design/composite/GaiaBanner.vue';
import GaiaInput from '@/components/design/base/inputs/GaiaInput.vue';
import VariantConstraintFields from '@/components/contractGeneration/questions/VariantConstraintFields.vue';
import { gaiaAccent, gaiaNeutral } from '@/components/design/themes/tokens';
import prettyBytes from 'pretty-bytes';
import { useGaiaToast } from '@/composables/useGaiaToast';

interface Props {
  show: boolean;
  templateUid: string;
  /** Names already taken on this template — each must be unique. */
  existingNames: string[];
  /**
   * When set, the modal opens in "replace" mode for this variant: the name
   * is prefilled and locked, the already-used guard is skipped, and the
   * submit posts a new vault file revision for the existing variant, which is
   * then re-read by this uid.
   */
  variantUid?: string | null;
  lockedVariantName?: string | null;
  /**
   * The template's questions, so a new document can say what it covers while
   * it is being added rather than arriving unanswered and flagged. Empty for a
   * template that asks nothing, and ignored in replace mode — the document
   * being replaced keeps the answers it already has.
   */
  questions?: ContractGenerationTemplateQuestionInDb[];
}

const props = withDefaults(defineProps<Props>(), {
  variantUid: null,
  lockedVariantName: null,
  questions: () => [],
});
const emit = defineEmits<{
  (e: 'update:show', value: boolean): void;
  (e: 'uploaded', file: ContractGenerationTemplateVariantInDb): void;
}>();

const message = useGaiaToast();

const name = ref<string>('');
const answers = ref<VariantAnswerDraftMap>({});
const fileRef = ref<UploadFileInfo | null>(null);
const submitting = ref<boolean>(false);
const error = ref<string | null>(null);

const showModal = computed({
  get: () => props.show,
  set: value => {
    emit('update:show', value);
  },
});

function resetForm(): void {
  name.value = props.lockedVariantName ?? '';
  answers.value = emptyAnswerDraft(props.questions);
  fileRef.value = null;
  error.value = null;
}

watch(
  () => props.show,
  value => {
    if (value) resetForm();
  }
);

const isReplaceMode = computed<boolean>(() => Boolean(props.variantUid && props.lockedVariantName));

/**
 * Asked only when adding: a replacement keeps the answers its document already
 * has, and re-asking would invite them to be silently dropped.
 */
const asksQuestions = computed<boolean>(() => !isReplaceMode.value && props.questions.length > 0);

const answersHaveError = computed<boolean>(() =>
  props.questions.some(
    question =>
      answerDraftError(question, answers.value[question.uid] ?? blankAnswerDraft()) !== null
  )
);

const canSubmit = computed(() => {
  if (submitting.value) return false;
  if (name.value.trim().length === 0) return false;
  if (!(fileRef.value?.file instanceof File)) return false;
  // Skip the "already used" check in replace mode — the whole point is to
  // overwrite the .docx for the existing variant.
  if (!isReplaceMode.value && nameAlreadyUsed.value) return false;
  if (asksQuestions.value && answersHaveError.value) return false;
  return true;
});

/** Names are compared case-insensitively: "German" and "german" would read as
 *  the same variant to anyone scanning the list, whatever the database says. */
const nameAlreadyUsed = computed(
  () =>
    !isReplaceMode.value &&
    name.value.trim().length > 0 &&
    props.existingNames.some(existing => existing.toLowerCase() === name.value.trim().toLowerCase())
);

const pickedFile = computed(() =>
  fileRef.value?.file instanceof File ? fileRef.value.file : null
);

function handleUploadChange(options: { fileList: UploadFileInfo[] }): void {
  fileRef.value = options.fileList.at(-1) ?? null;
}

function removeFile(): void {
  fileRef.value = null;
}

function handleCustomUpload({ onFinish }: UploadCustomRequestOptions): void {
  onFinish();
}

async function submit(): Promise<void> {
  if (!canSubmit.value) return;
  const file = fileRef.value?.file;
  if (!(file instanceof File)) return;

  submitting.value = true;
  error.value = null;

  try {
    let result: ContractGenerationTemplateVariantInDb;

    if (isReplaceMode.value && props.variantUid) {
      // "Upload fixed version" — a new revision of the variant's source; the
      // variant comes back with its `template_status` flipped to validating.
      result = await TemplateVariantsService.replaceFile(props.templateUid, props.variantUid, file);
      message.success(`Uploaded new version — re-validating “${result.name}”…`);
    } else {
      // "Add variant" — the file and the new variant row in one request.
      result = await TemplateVariantsService.create(props.templateUid, file, name.value.trim());

      // Answers are their own write — variant creation takes the file and the
      // name, nothing else. The document exists either way, so a failure here
      // is reported without losing the upload: its answers can still be set
      // from its card.
      if (asksQuestions.value) {
        const payload = toVariantAnswerPayload(props.questions, answers.value);
        if (payload.length > 0) {
          try {
            await TemplateQuestionsService.setVariantAnswers(
              props.templateUid,
              result.uid,
              payload
            );
          } catch (answersError) {
            message.warning(
              extractErrorDetail(
                answersError,
                'Uploaded, but its answers could not be saved — set them from the document’s card.'
              )
            );
          }
        }
      }

      message.success('Uploaded — validating template…');
    }

    emit('uploaded', result);
    emit('update:show', false);
  } catch (error_) {
    error.value = extractErrorDetail(error_, 'Failed to upload this variant.');
  } finally {
    submitting.value = false;
  }
}

/**
 * Inline custom properties rather than `v-bind()` — the modal root is a
 * Teleport, so Vue has no root element in place to write `v-bind()` vars onto.
 */
const styleVars = {
  '--variant-upload-border-soft': gaiaNeutral.borderSoft,
  '--variant-upload-card-border': gaiaNeutral.borderCard,
  '--variant-upload-soft-surface': gaiaNeutral.surfaceTint,
  '--variant-upload-accent': gaiaAccent.base,
};
</script>

<template>
  <gaia-modal
    v-model:show="showModal"
    width="480px"
    :mask-closable="!submitting"
    :closable="!submitting"
    :style="styleVars"
  >
    <template #header>
      <n-flex vertical :size="6">
        <gaia-text size="subtitle" weight="semibold" tone="ink">
          {{ isReplaceMode ? `Upload fixed version (${lockedVariantName})` : 'Add variant' }}
        </gaia-text>
        <gaia-text size="row" tone="text">
          Upload the .docx for this variant — its placeholders are extracted and validated
          automatically.
        </gaia-text>
      </n-flex>
    </template>

    <n-flex vertical :size="14" class="upload-body">
      <gaia-banner v-if="error" type="error">{{ error }}</gaia-banner>

      <gaia-form-field label="Variant name" required hint="a language, a country, a wording">
        <gaia-input
          v-model:value="name"
          placeholder="e.g. German, Freelancer (Germany), Male wording"
          :disabled="isReplaceMode"
          style="width: 100%"
        />
      </gaia-form-field>

      <gaia-banner v-if="nameAlreadyUsed" type="warning">
        “{{ name.trim() }}” is already a variant of this template — use its detail page to upload a
        new version instead.
      </gaia-banner>

      <gaia-form-field label=".docx source file" required>
        <div v-if="pickedFile" class="picked-file">
          <svg
            width="15"
            height="15"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            stroke-width="1.4"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="picked-file__icon"
          >
            <path d="M4 2.5h5l3 3v8H4z" />
            <path d="M9 2.5v3h3" />
          </svg>
          <n-flex vertical :size="1" :style="{ minWidth: 0, flex: 1 }">
            <gaia-text truncate size="dense" weight="semibold" tone="ink">
              {{ pickedFile.name }}
            </gaia-text>
            <gaia-text size="micro" tone="faint">{{ prettyBytes(pickedFile.size) }}</gaia-text>
          </n-flex>
          <gaia-delete-button
            intent="remove"
            subject="file"
            :disabled="submitting"
            @click="removeFile"
          />
        </div>

        <gaia-file-upload
          v-else
          variant="dropzone"
          :default-upload="false"
          :multiple="false"
          :custom-request="handleCustomUpload"
          :show-file-list="false"
          accept=".docx"
          @change="handleUploadChange"
        >
          Click or drag a .docx file here
          <template #description>Template placeholders will be extracted automatically.</template>
        </gaia-file-upload>
      </gaia-form-field>

      <!-- What this document covers, asked while it is being added. A document
           that arrives answering nothing is a candidate for every combination,
           and any question marked as required holds the whole template back —
           so the moment to ask is now, not after it lands flagged. -->
      <template v-if="asksQuestions">
        <n-flex vertical :size="3" class="upload-section">
          <gaia-text size="control" weight="semibold" tone="ink">What it covers</gaia-text>
          <gaia-text size="meta" tone="subtle">
            Answer what applies. Anything left blank isn’t used to narrow, and can be set later.
          </gaia-text>
        </n-flex>
        <variant-constraint-fields
          :questions="props.questions"
          :draft="answers"
          :disabled="submitting"
          @update:draft="(value: VariantAnswerDraftMap) => (answers = value)"
        />
      </template>
    </n-flex>

    <template #footer>
      <n-flex align="center" :size="8">
        <gaia-text size="micro" tone="faint">
          Validation runs in the background after upload.
        </gaia-text>
        <gaia-button
          size="small"
          :disabled="submitting"
          :style="{ marginLeft: 'auto' }"
          @click="emit('update:show', false)"
        >
          Cancel
        </gaia-button>
        <gaia-button
          type="primary"
          size="small"
          :loading="submitting"
          :disabled="!canSubmit"
          @click="submit"
        >
          {{ isReplaceMode ? 'Replace' : 'Upload' }}
        </gaia-button>
      </n-flex>
    </template>
  </gaia-modal>
</template>

<style scoped>
/* A template with several questions makes this dialog taller than the screen.
   The body scrolls on its own so the footer — and the button that closes it —
   stay reachable. Capped against the viewport rather than a fixed height, so a
   short form still sizes to its content. */
.upload-body {
  max-height: min(62vh, 560px);
  overflow-y: auto;
  /* Room for the focus ring on the last field, which the scroll box clips. */
  padding: 2px 2px 4px;
  margin: -2px -2px -4px;
}

/* Separates the upload from what the document covers. A hairline, not the
   divider's full rule — this is one form, not two. */
.upload-section {
  border-top: 1px solid var(--variant-upload-border-soft);
  padding-top: 14px;
  margin-top: 2px;
}

/* Colours arrive through the styleVars map on the card, not v-bind() — the
   modal's teleported subtree never receives component-root CSS vars. */
.picked-file {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 12px;
  border: 1px solid var(--variant-upload-card-border);
  border-radius: 10px;
  background: var(--variant-upload-soft-surface);
}

.picked-file__icon {
  flex: none;
  color: var(--variant-upload-accent);
}
</style>
